import { CameraOutlined, CloseOutlined, LoadingOutlined, PictureOutlined, UploadOutlined } from '@ant-design/icons';
import { Alert, Button, Flex, Progress, Space, Typography } from 'antd';
import { ArtifactMediaFieldState } from 'components/Artifacts/editorTypes';
import { ArtifactMedia } from 'components/Artifacts/types';
import {
  ArtifactMediaValidationErrorCode,
  getArtifactMediaConstraint,
  validateArtifactMediaFile
} from 'constants/artifactMediaConstraints';
import { useEffect, useRef } from 'react';
import { bytesToSize } from 'utils/utils';

import { useArtifactMediaUpload } from './useArtifactMediaUpload';

const { Paragraph, Text } = Typography;

type ArtifactImageFieldProps = {
  initialMedia?: ArtifactMedia | null;
  onChange: (state: ArtifactMediaFieldState) => void;
};

const getErrorMessage = (errorCode: ArtifactMediaValidationErrorCode, maxFileSize: number) => {
  if (errorCode === 'file_too_large') {
    return `Image size should be less than ${bytesToSize(maxFileSize)}.`;
  }

  return 'Unsupported image type.';
};

const sectionBadgeStyle = {
  alignItems: 'center',
  background: 'linear-gradient(180deg, #7c6cff 0%, #5f4bf4 100%)',
  borderRadius: '999px',
  color: '#fff',
  display: 'inline-flex',
  fontSize: 12,
  fontWeight: 700,
  height: 20,
  justifyContent: 'center',
  minWidth: 20
} as const;

const requiredStarStyle = {
  color: '#ff4d4f',
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1
} as const;

export function ArtifactImageField({ initialMedia = null, onChange }: ArtifactImageFieldProps) {
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const captureInputRef = useRef<HTMLInputElement | null>(null);
  const mediaConstraint = getArtifactMediaConstraint('artifactImage');
  const { state, uploadFile, reset, setError } = useArtifactMediaUpload({
    mediaKind: 'IMAGE',
    initialMediaId: initialMedia?.id,
    initialMediaUrl: initialMedia?.url || initialMedia?.thumbnailUrl || null
  });

  useEffect(() => {
    onChange(state);
  }, [onChange, state]);

  const handleSelectedFile = async (file?: File | null) => {
    if (!file) {
      return;
    }

    const validationResult = await validateArtifactMediaFile(file, 'artifactImage');
    if (!validationResult.isValid) {
      setError(getErrorMessage(validationResult.errorCode, mediaConstraint.maxFileSize));
      return;
    }

    await uploadFile(file);
  };

  const previewUrl = state.previewUrl || state.media?.thumbnailUrl || state.media?.url || '';
  const hasPreview = Boolean(previewUrl);
  const helperCopy = 'JPG, PNG, HEIC, HEIF, or WEBP up to 20MB. Recommended 4:3 or 16:9.';

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #f1f2f8',
        borderRadius: 16,
        padding: 16
      }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Flex align="center" justify="space-between" gap={12} style={{ width: '100%' }}>
          <Flex align="center" gap={8}>
            <span style={sectionBadgeStyle}>1</span>
            <Text style={{ color: '#26233a', fontSize: 15, fontWeight: 600 }}>Photo</Text>
          </Flex>
          <span aria-label="Photo is required" title="Photo is required" style={requiredStarStyle}>
            *
          </span>
        </Flex>

        {state.errorMessage && <Alert type="error" showIcon message={state.errorMessage} />}

        <Space direction="vertical" size={14} style={{ width: '100%' }}>
          <div
            style={{
              background: hasPreview ? '#f7f5ff' : 'linear-gradient(180deg, #fdfcff 0%, #faf8ff 100%)',
              border: hasPreview ? '1px solid #e7e1ff' : '1px dashed #d8cef9',
              borderRadius: 18,
              minHeight: 220,
              overflow: 'hidden',
              padding: 18,
              position: 'relative'
            }}
          >
            {hasPreview ? (
              <>
                <img
                  src={previewUrl}
                  alt="Artifact preview"
                  style={{ display: 'block', height: '100%', maxHeight: 260, objectFit: 'contain', width: '100%' }}
                />
                <Button
                  aria-label="Remove image"
                  icon={<CloseOutlined />}
                  onClick={reset}
                  shape="circle"
                  size="small"
                  style={{ position: 'absolute', right: 12, top: 12 }}
                />
              </>
            ) : (
              <Flex
                align="center"
                justify="center"
                vertical
                style={{ color: '#7f7aa5', gap: 10, height: '100%', minHeight: 182, padding: 24, textAlign: 'center' }}
              >
                <div
                  aria-hidden="true"
                  style={{
                    alignItems: 'center',
                    background: 'linear-gradient(180deg, rgba(124, 108, 255, 0.14) 0%, rgba(95, 75, 244, 0.08) 100%)',
                    borderRadius: 18,
                    color: '#8d83ef',
                    display: 'flex',
                    height: 84,
                    justifyContent: 'center',
                    width: 84
                  }}
                >
                  <PictureOutlined style={{ fontSize: 38 }} />
                </div>
                <Text style={{ color: '#2d2950', fontSize: 18, fontWeight: 600 }}>No photo added yet</Text>
                <Paragraph style={{ color: '#847d9c', marginBottom: 0, maxWidth: 240 }}>
                  Add a photo to accompany this memory.
                </Paragraph>
              </Flex>
            )}
          </div>

          <Flex gap={12} wrap="wrap" align="stretch">
            <Button
              icon={<UploadOutlined />}
              onClick={() => uploadInputRef.current?.click()}
              style={{
                borderColor: '#cdc3ff',
                borderRadius: 12,
                color: '#5f4bf4',
                flex: '1 1 220px',
                fontWeight: 600,
                height: 44
              }}
            >
              {state.media ? 'Replace Photo' : 'Upload Photo'}
            </Button>
            <Button
              icon={<CameraOutlined />}
              onClick={() => captureInputRef.current?.click()}
              style={{
                borderColor: '#e4def9',
                borderRadius: 12,
                color: '#3f3a63',
                flex: '1 1 220px',
                fontWeight: 500,
                height: 44
              }}
            >
              Take Photo
            </Button>
          </Flex>

          <Text type="secondary" style={{ display: 'block', fontSize: 12, textAlign: 'center' }}>
            {helperCopy}
          </Text>
        </Space>

        {(state.phase === 'uploading' || state.phase === 'processing') && (
          <div>
            <Text>{state.phase === 'uploading' ? 'Uploading image' : 'Processing image'}</Text>
            <Progress percent={state.progressPercent} size="small" />
          </div>
        )}

        {(state.phase === 'uploading' || state.phase === 'processing') && <LoadingOutlined style={{ color: '#6d5efc' }} />}
      </Space>

      <input
        ref={uploadInputRef}
        type="file"
        accept={mediaConstraint.accept}
        style={{ display: 'none' }}
        onChange={(event) => void handleSelectedFile(event.target.files?.[0] || null)}
      />
      <input
        ref={captureInputRef}
        type="file"
        accept={mediaConstraint.accept}
        capture={mediaConstraint.capture as 'environment'}
        style={{ display: 'none' }}
        onChange={(event) => void handleSelectedFile(event.target.files?.[0] || null)}
      />
    </div>
  );
}

export default ArtifactImageField;